import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Chip,
  Typography,
} from "@mui/material";
import { IconPencil, IconX } from "@tabler/icons-react";
import GridComponent from "../../shared-components/gridComponent";
import SnackState from "../../shared-components/snackbar/snackState";
import CommonSnackBar from "../../shared-components/snackbar/commonSnackbar";
import UsersModal from "./UsersModal";
import { trackPromise } from "react-promise-tracker";
import apiClient from "../../lib/api";
import urlConstant from "../../constants/urlConstants";
import dataConstant from "../../constants/dataConstants";
import { selectUserDetail } from "../../redux/slice/userSlice";
import { useSelector } from "react-redux";
import ResetPasswordModal from "./ResetPasswordModal";
import { encrypt } from "../../Helper/encryptHelper";

const UsersList = () => {
  const [gridKey, setGridKey] = useState(false);
  const [snack, closeSnack, showSnackbar] = SnackState();
  const [openPopup, setOpenPopup] = useState(false);
  const [openModalId, setOpenModalId] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [cityData, setCityData] = useState([]);
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const getUserDetail = useSelector(selectUserDetail);
  const refreshGrid = () => setGridKey((prev) => !prev);
  const [selectedUser, setSelectedUser] = useState(null);
  const [statusModalOpen, setStatusModalOpen] = useState(false);

  useEffect(() => {
    apiClient.get(urlConstant.City.GetAllCities, {authRequired: true}).then((response) =>{
        if(response.status === dataConstant.APIStatus.Success){
          setCityData(response.data)
        }
      })
  },[])
 
  const getGridData = async ({
    page,
    pageSize,
    sortModel,
    searchText,
    filters,
  }) => {
    const sort =
      sortModel?.length > 0
        ? {
            sortColumnName: sortModel[0].field,
            sortOrder: sortModel[0].sort === "asc" ? "asc" : "desc",
          }
        : {
            sortColumnName: "createdBy",
            sortOrder: "desc",
          };

    const requestObject = {
      PagingInfoVM: {
        pageNumber: page,
        rowsPerPage: pageSize,
        searchText: searchText || "",
        sortColumns: sort,
      },
    };
    if (filters?.role) {
      requestObject.RoleName = filters?.role;
    }
    if (filters?.status) {
      requestObject.Status = filters?.status;
    }
    try {
      const response = await trackPromise(
        apiClient.post(urlConstant.Login.GetUserList, requestObject, {
          authRequired: true,
        })
      );

      if (response?.status !== dataConstant.APIStatus.Success) {
        showSnackbar(response?.message || "Failed to fetch users", "error");
        return { items: [], totalCount: 0 };
      }

      return {
        items: response.data?.userList || [],
        totalCount: response.data?.totalCount || 0,
      };
    } catch (error) {
      console.error("Error fetching users:", error);
      showSnackbar("Error fetching users", "error");
      return { items: [], totalCount: 0 };
    }
  };

  const advanceFiltersConfig = [
    {
      type: "select",
      label: "Role",
      name: "role",
      options: [
        { id: 1, name: "Admin" },
        { id: 2, name: "Staff" },
        { id: 3, name: "Marketer" },
      ],
    },
    {
      type: "select",
      label: "Status",
      name: "status",
      options: [
        { id: 1, name: "Active" },
        { id: 2, name: "Inactive" },
      ],
    },
  ];
  const handleDelete = (row) => {
    setDeleteRow(row);
    setDeleteDialogOpen(true);
  };

  const handleStatusChange = async (user) => {
    const apiUrl = urlConstant.Login.UserStatusUpdate;
    const data = {
      id: user?.id,
      status: user?.status === "Active" ? "Inactive" : "Active",
      loginUser: getUserDetail?.userId,
    };
    trackPromise(
      apiClient
        .post(apiUrl, data)
        .then((response) => {
          const success =
            response?.statusCode === 200 && response?.status !== "Failure";

          if (success) {
            showSnackbar(response?.message, "success");
            setStatusModalOpen(false);
            refreshGrid(); // Reload table/grid
          } else {
            showSnackbar(response?.message || "Operation failed", "error");
          }
        })
        .catch((error) => {
          const errMsg = error?.response?.data?.message || "Server error";
          showSnackbar(errMsg, "error");
        })
    );
  };

  const handleStatusClick = (user) => {
    setSelectedUser(user);
    setStatusModalOpen(true);
  };
  const confirmDelete = async () => {
    try {
      if (!deleteRow?.email) {
        showSnackbar("User ID missing for deletion", "error");
        return;
      }

      const deleteUrl = `${urlConstant.Login.DeleteUser}?id=${deleteRow.email}&loginUser=${getUserDetail?.userId}`;

      const response = await trackPromise(apiClient.get(deleteUrl));

      if (response?.statusCode === 200 || response?.status !== "Success") {
        showSnackbar(`Deleted ${deleteRow.fullName}`, "success");
        refreshGrid();
      } else {
        showSnackbar("Failed to delete user", "error");
      }
    } catch (error) {
      console.error("Delete error", error);
      showSnackbar("Server error during delete", "error");
    } finally {
      setDeleteDialogOpen(false);
      setDeleteRow(null);
    }
  };

  const handleAdd = () => {
    setSelectedRow(null);
    setOpenPopup(true);
  };

  const handleEdit = (row) => {
    setSelectedRow(row);
    setOpenPopup(true);
  };

  const handleSave = (data) => {
    const isUpdate = !!data.id; // Check if user has ID => it's an update
    const apiUrl = isUpdate
      ? urlConstant.Login.UpdateUser
      : urlConstant.Login.AddUser;
    data.loginUser = getUserDetail?.userId;
    trackPromise(
      apiClient
        .post(apiUrl, data)
        .then((response) => {
          const success =
            response?.statusCode === 200 && response?.status !== "Failure";

          if (success) {
            showSnackbar(response?.message, "success");
            setOpenPopup(false); // Close modal
            refreshGrid(); // Reload table/grid
          } else {
            showSnackbar(response?.message || "Operation failed", "error");
          }
        })
        .catch((error) => {
          const errMsg = error?.response?.data?.message || "Server error";
          showSnackbar(errMsg, "error");
        })
    );
  };

  const handleReset = (data) => {
    const ResetUserPasswordUrl = `${urlConstant.Login.ResetUserPassword}?id=${
      data.id
    }&password=${encrypt(data.password)}&UpdatedBy=${getUserDetail.userId}`;
    trackPromise(
      apiClient
        .get(ResetUserPasswordUrl)
        .then((response) => {
          const success =
            response?.statusCode === 200 && response?.status !== "Failure";

          if (success) {
            showSnackbar(response?.message, "success");
            setOpenModalId(false);
            refreshGrid();
          } else {
            setOpenModalId(true);
            showSnackbar(response?.message || "Operation failed", "error");
          }
        })
        .catch((error) => {
          const errMsg = error?.response?.data?.message || "Server error";
          showSnackbar(errMsg, "error");
        })
    );
  };

  const columns = [
    { field: "fullName", headerName: "Name", flex: 1 },
    { field: "email", headerName: "Email", flex: 1.75 },
    { field: "phoneNumber", headerName: "Phone", flex: 1 },
    { field: "roleName", headerName: "Role", flex: 0.5 },
    {
      field: "status",
      headerName: "Status",
      flex: 0.75,
      renderCell: (params) => (
        <Chip
          label={params.row.status}
          size="small"
          clickable
          onClick={() => handleStatusClick(params.row)}
          color={params.row.status === "Active" ? "success" : "error"}
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1.75,
      renderCell: (params) => (
        <>
          {getUserDetail.roles?.includes("Admin") && (
            <Button
              variant="contained"
              onClick={() => setOpenModalId(params.row.id)}
            >
              Reset Password
            </Button>
          )}
          <ResetPasswordModal
            open={openModalId === params.row.id}
            onClose={() => setOpenModalId(null)}
            userId={params.row.id}
            onReset={handleReset}
          />
          <IconButton onClick={() => handleEdit(params.row)}>
            <IconPencil stroke={1.5} />
          </IconButton>
          {getUserDetail?.userId !== params.row.id && (
            <IconButton onClick={() => handleDelete(params.row)}>
              <IconX stroke={1.5} />
            </IconButton>
          )}
        </>
      ),
    },
  ];

  return (
    <>
      <GridComponent
        columns={columns}
        getGridData={getGridData}
        gridKey={gridKey}
        isAddButton={true}
        addButtonTitle="Add User"
        onAdd={handleAdd}
        pagination
        sorting
        search
        isRequiredAdvanceFilter={true}
        advanceFiltersConfig={advanceFiltersConfig}
      />

      <UsersModal
        open={openPopup}
        onClose={() => setOpenPopup(false)}
        onSave={handleSave}
        selectedRow={selectedRow}
        cityData={cityData}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
      >
        <DialogContent>
          <DialogContentText
            typography="h5"
            color="default.main"
            textAlign="center"
          >
            Are you sure you want to delete <strong>{deleteRow?.name}</strong>?
          </DialogContentText>
          <DialogActions
            sx={{ padding: 0, justifyContent: "center", mt: 4, border: "none" }}
          >
            <Button
              size="small"
              onClick={confirmDelete}
              color="primary"
              variant="contained"
            >
              Yes
            </Button>
            <Button
              size="small"
              color="primary"
              variant="outlined"
              onClick={() => setDeleteDialogOpen(false)}
            >
              No
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>

      <Dialog
        open={statusModalOpen}
        onClose={() => setStatusModalOpen(false)}
        maxWidth="xs"
      >
        <DialogContent>
          <DialogContentText
            typography="h5"
            color="default.main"
            textAlign="center"
          >
            Are you sure you want to{" "}
            <Typography
              component="span"
              variant="h5"
              color={
                selectedUser?.status === "Active"
                  ? "error.main"
                  : "success.main"
              }
            >
              {selectedUser?.status === "Active" ? "Inactive" : "Active"}
            </Typography>{" "}
            user <strong>{selectedUser?.fullName}</strong>?
          </DialogContentText>
          <DialogActions
            sx={{ padding: 0, justifyContent: "center", mt: 4, border: "none" }}
          >
            <Button
              size="small"
              onClick={() => handleStatusChange(selectedUser)}
              color="primary"
              variant="contained"
            >
              {selectedUser?.status === "Active" ? "Deactivate" : "Activate"}
            </Button>
            <Button
              size="small"
              color="primary"
              variant="outlined"
              onClick={() => setStatusModalOpen(false)}
            >
              No
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>

      <CommonSnackBar
        snackObj={snack}
        closeSnack={closeSnack}
        duration={2000}
      />
    </>
  );
};

export default UsersList;
